import common from "../../common";
import Scene from "../../scenes/Scene";
import GelButton from "./GelButton";
import GelMuteButton from "./GelMuteButton";
import GelToggleButton from "./GelToggleButton";

export default class GelScene extends Scene {
    constructor() {
        super();

        this._title = null;

        this.signals.character = new P3.utils.MiniSignal();
        this.signals.help = new P3.utils.MiniSignal();
        this.signals.settings = new P3.utils.MiniSignal();
        this.signals.achievements = new P3.utils.MiniSignal();
        this.signals.restart = new P3.utils.MiniSignal();
        this.signals.exit = new P3.utils.MiniSignal();
        this.signals.calculator = new P3.utils.MiniSignal();

        this._gelButtons = [];

        this._playButton = null;
        this._nextButton = null;
        this._restartButton = null;
        this._pauseButton = null;
        this._helpButton = null;
        this._characterButton = null;
        this._homeButton = null;
        this._backButton = null;
        this._exitButton = null;
        this._muteButton = null;
        this._settingsButton = null;
        this._achievementsButton = null;
        this._leftButton = null;
        this._rightButton = null;
        this._skipButton = null;
        this._userSlot1Button = null;
        this._userSlot2Button = null;
        this._userSlot3Button = null;
        this._calculatorButton = null;
    }

    init() {
        super.init();
    }

    destroy() {
        for (let i = 0; i < this._gelButtons.length; ++i) {
            const button = this._gelButtons[i];
            button.destroy();
        }
        this._gelButtons.length = 0;

        P3.audio.stopVO();

        super.destroy();
    }

    show() {
        super.show();

        this.setAllButtonsVisible(true);
        this.setAllButtonsEnabled(true);
        this.restoreAllButtonsTabIndex();

        this._muteButton && this._muteButton.updateTextures();
    }

    hide() {
        super.hide();

        this.setAllButtonsVisible(false);
    }

    animateIn(callback, scope) {
        if (this._title) {
            this._title.scale.set(0);
            new P3.utils.TWEEN.Tween(this._title.scale, this._tweens)
                .to({x: 1, y: 1}, 400)
                .easing(P3.utils.TWEEN.Easing.Back.Out)
                .onStart(() => {
                    this._title.visible = true;
                })
                .start();
        }
    }

    initTitle(text, size = 40) {
        this._title = new PIXI.Sprite(P3.assets.texture("ui_title_bg"));
        this._title.position.set(common.STAGE_WIDTH * 0.5, 94);
        this._title.anchor.set(0.5);
        this._title.visible = false;
        this.addChild(this._title);

        this._title.label = new PIXI.extras.BitmapText(text, {font: size + "px BBC Reith Sans"});
        this._title.label.position.set(0, -15);
        this._title.label.anchor.set(0.5);
        this._title.label.tint = 0xFFFFFF;
        this._title.addChild(this._title.label);
    }

    initPlayButton(tabIndex, position = GelButton.POSITION_MIDDLE_LOW, size = GelScene.SIZE_LARGE) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_" + size + "_normal");
        states.over = P3.assets.texture("btn_primary_" + size + "_over");
        states.down = P3.assets.texture("btn_primary_" + size + "_down");
        states.icon = P3.assets.texture("btn_icon_play_large");

        this._playButton = new GelButton(states);
        this._playButton.position = position;
        this._playButton.tabIndex = tabIndex;
        this._playButton.ariaLabel = "Play";
        this._playButton.overSoundName = "sfx_btn_rollover_00";
        this._playButton.clickSoundName = "sfx_btn_press_00";
        this._playButton.voSoundName = "vo_queen_103a";
        this._playButton.signals.click.add(this.onPlayButtonClick, this);
        this._gelButtons.push(this._playButton);
    }

    initNextButton(tabIndex, position = GelButton.POSITION_MIDDLE_LOW) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_large_normal");
        states.over = P3.assets.texture("btn_primary_large_over");
        states.down = P3.assets.texture("btn_primary_large_down");
        states.icon = P3.assets.texture("btn_icon_next_large");

        this._nextButton = new GelButton(states);
        this._nextButton.position = position;
        this._nextButton.tabIndex = tabIndex;
        this._nextButton.ariaLabel = "Next";
        this._nextButton.overSoundName = "sfx_btn_rollover_00";
        this._nextButton.clickSoundName = "sfx_btn_press_00";
        this._nextButton.voSoundName = "vo_queen_103a";
        this._nextButton.signals.click.add(this.onNextButtonClick, this);
        this._gelButtons.push(this._nextButton);
    }

    initRestartButton(tabIndex, position = GelButton.POSITION_MIDDLE_TWO_LEFT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_medium_normal");
        states.over = P3.assets.texture("btn_primary_medium_over");
        states.down = P3.assets.texture("btn_primary_medium_down");
        states.icon = P3.assets.texture("btn_icon_restart_medium");

        this._restartButton = new GelButton(states);
        this._restartButton.position = position;
        this._restartButton.tabIndex = tabIndex;
        this._restartButton.ariaLabel = "Restart";
        this._restartButton.overSoundName = "sfx_btn_rollover_00";
        this._restartButton.clickSoundName = "sfx_btn_press_00";
        this._restartButton.voSoundName = "vo_queen_123";
        this._restartButton.signals.click.add(this.onRestartButtonClick, this);
        this._gelButtons.push(this._restartButton);
    }

    initPauseButton(tabIndex, position = GelButton.POSITION_TOP_RIGHT_FIRST) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_small_normal");
        states.over = P3.assets.texture("btn_primary_small_over");
        states.down = P3.assets.texture("btn_primary_small_down");
        states.icon = P3.assets.texture("btn_icon_pause_small");

        this._pauseButton = new GelButton(states);
        this._pauseButton.position = position;
        this._pauseButton.tabIndex = tabIndex;
        this._pauseButton.ariaLabel = "Pause";
        this._pauseButton.overSoundName = "sfx_btn_rollover_00";
        this._pauseButton.clickSoundName = "sfx_btn_press_00";
        this._pauseButton.voSoundName = "vo_queen_120a";
        this._pauseButton.signals.click.add(this.onPauseButtonClick, this);
        this._gelButtons.push(this._pauseButton);
    }

    initHelpButton(tabIndex, position = GelButton.POSITION_BOTTOM_RIGHT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_secondary_rectwide_normal");
        states.over = P3.assets.texture("btn_secondary_rectwide_over");
        states.down = P3.assets.texture("btn_secondary_rectwide_down");
        states.icon = P3.assets.texture("btn_icon_howtoplay");

        this._helpButton = new GelButton(states);
        this._helpButton.position = position;
        this._helpButton.tabIndex = tabIndex;
        this._helpButton.ariaLabel = "How to play";
        this._helpButton.animateIcon = false;
        this._helpButton.overSoundName = "sfx_btn_rollover_00";
        this._helpButton.clickSoundName = "sfx_btn_press_00";
        this._helpButton.voSoundName = "vo_queen_105b";
        this._helpButton.signals.click.add(this.onHelpButtonClick, this);
        this._gelButtons.push(this._helpButton);
    }

    initCharacterButton(tabIndex, position = GelButton.POSITION_BOTTOM_LEFT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_secondary_rectangle_248_off");
        states.over = P3.assets.texture("btn_secondary_rectangle_248_over");
        states.down = P3.assets.texture("btn_secondary_rectangle_248_off");

        this._characterButton = new GelButton(states);
        this._characterButton.position = position;
        this._characterButton.tabIndex = tabIndex;
        this._characterButton.setText("Choose Character", 24);
        this._characterButton.overSoundName = "sfx_btn_rollover_00";
        this._characterButton.clickSoundName = "sfx_btn_press_00";
        this._characterButton.signals.click.add(this.onCharacterButtonClick, this);
        this._gelButtons.push(this._characterButton);
    }

    initHomeButton(tabIndex, position = GelButton.POSITION_TOP_LEFT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_small_normal");
        states.over = P3.assets.texture("btn_primary_small_over");
        states.down = P3.assets.texture("btn_primary_small_down");
        states.icon = P3.assets.texture("btn_icon_home_small");

        this._homeButton = new GelButton(states);
        this._homeButton.position = position;
        this._homeButton.tabIndex = tabIndex;
        this._homeButton.ariaLabel = "Home";
        this._homeButton.overSoundName = "sfx_btn_rollover_00";
        this._homeButton.clickSoundName = "sfx_btn_press_00";
        this._homeButton.voSoundName = "vo_queen_104a";
        this._homeButton.signals.click.add(this.onHomeButtonClick, this);
        this._gelButtons.push(this._homeButton);
    }

    initBackButton(tabIndex, position = GelButton.POSITION_TOP_LEFT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_small_normal");
        states.over = P3.assets.texture("btn_primary_small_over");
        states.down = P3.assets.texture("btn_primary_small_down");
        states.icon = P3.assets.texture("btn_icon_back_small");

        this._backButton = new GelButton(states);
        this._backButton.position = position;
        this._backButton.tabIndex = tabIndex;
        this._backButton.ariaLabel = "Back";
        this._backButton.overSoundName = "sfx_btn_rollover_00";
        this._backButton.clickSoundName = "sfx_btn_press_00";
        this._backButton.voSoundName = "vo_queen_163";
        this._backButton.signals.click.add(this.onBackButtonClick, this);
        this._gelButtons.push(this._backButton);
    }

    initExitButton(tabIndex, position = GelButton.POSITION_TOP_LEFT) {
        if (!common.gmi.shouldShowExitButton) return;

        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_small_normal");
        states.over = P3.assets.texture("btn_primary_small_over");
        states.down = P3.assets.texture("btn_primary_small_down");
        states.icon = P3.assets.texture("btn_icon_exit_small");

        this._exitButton = new GelButton(states);
        this._exitButton.position = position;
        this._exitButton.tabIndex = tabIndex;
        this._exitButton.ariaLabel = "Exit";
        this._exitButton.overSoundName = "sfx_btn_rollover_00";
        this._exitButton.clickSoundName = "sfx_btn_press_00";
        this._exitButton.voSoundName = "vo_queen_163";
        this._exitButton.signals.click.add(this.onExitButtonClick, this);
        this._gelButtons.push(this._exitButton);
    }

    initSettingsButton(tabIndex, position = GelButton.POSITION_TOP_RIGHT_FIRST) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_tertiary_small_normal");
        states.over = P3.assets.texture("btn_tertiary_small_over");
        states.down = P3.assets.texture("btn_tertiary_small_down");
        states.icon = P3.assets.texture("btn_icon_settings_small");

        this._settingsButton = new GelButton(states);
        this._settingsButton.position = position;
        this._settingsButton.tabIndex = tabIndex;
        this._settingsButton.ariaLabel = "Settings";
        this._settingsButton.overSoundName = "sfx_btn_rollover_00";
        this._settingsButton.clickSoundName = "sfx_btn_press_00";
        this._settingsButton.voSoundName = "vo_queen_106b";
        this._settingsButton.signals.click.add(this.onSettingsButtonClick, this);
        this._gelButtons.push(this._settingsButton);
    }

    initMuteButton(tabIndex, position = GelButton.POSITION_TOP_RIGHT_SECOND) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_tertiary_small_normal");
        states.over = P3.assets.texture("btn_tertiary_small_over");
        states.down = P3.assets.texture("btn_tertiary_small_down");
        states.icon = P3.assets.texture("btn_icon_audio_small");
        states.iconAlt = P3.assets.texture("btn_icon_mute_small");

        this._muteButton = new GelMuteButton(states);
        this._muteButton.position = position;
        this._muteButton.tabIndex = tabIndex;
        this._muteButton.ariaLabel = "Mute";
        this._muteButton.overSoundName = "sfx_btn_rollover_00";
        this._muteButton.clickSoundName = "sfx_btn_press_00";
        this._muteButton.voSoundName = "vo_queen_107";
        this._muteButton.signals.click.add(this.onMuteButtonClick, this);
        this._gelButtons.push(this._muteButton);
    }

    initAchievementsButton(tabIndex, position = GelButton.POSITION_TOP_RIGHT_THIRD) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_tertiary_small_normal");
        states.over = P3.assets.texture("btn_tertiary_small_over");
        states.down = P3.assets.texture("btn_tertiary_small_down");
        states.icon = P3.assets.texture("btn_icon_achievements_small");

        this._achievementsButton = new GelButton(states);
        this._achievementsButton.position = position;
        this._achievementsButton.tabIndex = tabIndex;
        this._achievementsButton.ariaLabel = "Achievements";
        this._achievementsButton.overSoundName = "sfx_btn_rollover_00";
        this._achievementsButton.clickSoundName = "sfx_btn_press_00";
        this._achievementsButton.voSoundName = "vo_queen_extra_01a";
        this._achievementsButton.signals.click.add(this.onAchievementsButtonClick, this);
        this._gelButtons.push(this._achievementsButton);
    }

    initLeftButton(tabIndex, position = GelButton.POSITION_LEFT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_medium_normal");
        states.over = P3.assets.texture("btn_primary_medium_over");
        states.down = P3.assets.texture("btn_primary_medium_down");
        states.icon = P3.assets.texture("btn_icon_previous_small");

        this._leftButton = new GelButton(states);
        this._leftButton.position = position;
        this._leftButton.tabIndex = tabIndex;
        this._leftButton.ariaLabel = "Previous";
        this._leftButton.overSoundName = "sfx_btn_rollover_00";
        this._leftButton.clickSoundName = "sfx_btn_press_00";
        this._leftButton.signals.click.add(this.onLeftButtonClick, this);
        this._gelButtons.push(this._leftButton);
    }

    initRightButton(tabIndex, position = GelButton.POSITION_RIGHT) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_medium_normal");
        states.over = P3.assets.texture("btn_primary_medium_over");
        states.down = P3.assets.texture("btn_primary_medium_down");
        states.icon = P3.assets.texture("btn_icon_next_small");

        this._rightButton = new GelButton(states);
        this._rightButton.position = position;
        this._rightButton.tabIndex = tabIndex;
        this._rightButton.ariaLabel = "Next";
        this._rightButton.overSoundName = "sfx_btn_rollover_00";
        this._rightButton.clickSoundName = "sfx_btn_press_00";
        this._rightButton.signals.click.add(this.onRightButtonClick, this);
        this._gelButtons.push(this._rightButton);
    }

    initSkipButton(tabIndex, position = GelButton.POSITION_BOTTOM_RIGHT, size = GelScene.SIZE_MEDIUM) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_" + size + "_normal");
        states.over = P3.assets.texture("btn_primary_" + size + "_over");
        states.down = P3.assets.texture("btn_primary_" + size + "_down");
        states.icon = P3.assets.texture("btn_icon_skip_" + size);

        this._skipButton = new GelButton(states);
        this._skipButton.position = position;
        this._skipButton.tabIndex = tabIndex;
        this._skipButton.ariaLabel = "Skip tutorial";
        this._skipButton.overSoundName = "sfx_btn_rollover_00";
        this._skipButton.clickSoundName = "sfx_btn_press_00";
        this._skipButton.signals.click.add(this.onSkipButtonClick, this);
        this._gelButtons.push(this._skipButton);
    }

    initUserSlot1Button(tabIndex, position = GelButton.POSITION_USER_SLOT_ONE) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_rectwide_normal");
        states.over = P3.assets.texture("btn_primary_rectwide_over");
        states.down = P3.assets.texture("btn_primary_rectwide_down");

        this._userSlot1Button = new GelButton(states);
        this._userSlot1Button.id = 0;
        this._userSlot1Button.position = position;
        this._userSlot1Button.tabIndex = tabIndex;
        this._userSlot1Button.setText("Save Slot #1", 28);
        this._userSlot1Button.overSoundName = "sfx_btn_rollover_00";
        this._userSlot1Button.clickSoundName = "sfx_btn_press_00";
        this._userSlot1Button.voSoundName = "vo_queen_112a";
        this._userSlot1Button.signals.click.add(this.onUserSlotButtonClick, this);
        this._gelButtons.push(this._userSlot1Button);
    }

    initUserSlot2Button(tabIndex, position = GelButton.POSITION_USER_SLOT_TWO) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_rectwide_normal");
        states.over = P3.assets.texture("btn_primary_rectwide_over");
        states.down = P3.assets.texture("btn_primary_rectwide_down");

        this._userSlot2Button = new GelButton(states);
        this._userSlot2Button.id = 1;
        this._userSlot2Button.position = position;
        this._userSlot2Button.tabIndex = tabIndex;
        this._userSlot2Button.setText("Save Slot #2", 28);
        this._userSlot2Button.overSoundName = "sfx_btn_rollover_00";
        this._userSlot2Button.clickSoundName = "sfx_btn_press_00";
        this._userSlot2Button.voSoundName = "vo_queen_112a";
        this._userSlot2Button.signals.click.add(this.onUserSlotButtonClick, this);
        this._gelButtons.push(this._userSlot2Button);
    }

    initUserSlot3Button(tabIndex, position = GelButton.POSITION_USER_SLOT_THREE) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_primary_rectwide_normal");
        states.over = P3.assets.texture("btn_primary_rectwide_over");
        states.down = P3.assets.texture("btn_primary_rectwide_down");

        this._userSlot3Button = new GelButton(states);
        this._userSlot3Button.id = 2;
        this._userSlot3Button.position = position;
        this._userSlot3Button.tabIndex = tabIndex;
        this._userSlot3Button.setText("Save Slot #3", 28);
        this._userSlot3Button.overSoundName = "sfx_btn_rollover_00";
        this._userSlot3Button.clickSoundName = "sfx_btn_press_00";
        this._userSlot3Button.voSoundName = "vo_queen_112a";
        this._userSlot3Button.signals.click.add(this.onUserSlotButtonClick, this);
        this._gelButtons.push(this._userSlot3Button);
    }

    initCalculatorButton(tabIndex, position = GelButton.POSITION_TOP_RIGHT_SECOND) {
        const states = new P3.ButtonStates();
        states.normal = P3.assets.texture("btn_calculator_normal");
        states.over = P3.assets.texture("btn_calculator_over");
        states.down = P3.assets.texture("btn_calculator_down");
        states.icon = P3.assets.texture("btn_icon_calculator");
        states.normalAlt = P3.assets.texture("btn_calculator_on");
        states.overAlt = P3.assets.texture("btn_calculator_over");
        states.downAlt = P3.assets.texture("btn_calculator_down");
        states.iconAlt = P3.assets.texture("btn_icon_calculator");

        this._calculatorButton = new GelToggleButton(states);
        this._calculatorButton.position = position;
        this._calculatorButton.tabIndex = tabIndex;
        this._calculatorButton.ariaLabel = "Keypad";
        this._calculatorButton.overSoundName = "sfx_btn_rollover_00";
        this._calculatorButton.clickSoundName = "sfx_btn_press_00";
        this._calculatorButton.signals.click.add(this.onCalculatorButtonClick, this);
        this._gelButtons.push(this._calculatorButton);
    }

    setAllButtonsEnabled(value) {
        for (let i = 0; i < this._gelButtons.length; ++ i) {
            this._gelButtons[i].enabled = value;
        }
    }

    setAllButtonsVisible(value) {
        for (let i = 0; i < this._gelButtons.length; ++ i) {
            this._gelButtons[i].visible = value;
        }
    }

    restoreAllButtonsTabIndex()
    {
        for (let i = 0; i < this._gelButtons.length; ++ i)
        {
            this._gelButtons[i].restoreTabIndex();
        }
    }

    onPlayButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.next.dispatch(this);
    }

    onNextButtonClick(button) {
        this.signals.next.dispatch(this);
    }

    onRestartButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.restart.dispatch(this);
    }

    onPauseButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.pause.dispatch(this);
    }

    onHelpButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.help.dispatch(this);
    }

    onCharacterButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.character.dispatch(this);
    }

    onHomeButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.home.dispatch(this);

        // tracking
        common.gmi.sendStatsEvent("home", "click");
    }

    onBackButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.previous.dispatch(this);
    }

    onExitButtonClick(button) {
        this.signals.exit.dispatch(this);
    }

    onSettingsButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.settings.dispatch(this);
    }

    onMuteButtonClick(button) {
        // tracking
        common.gmi.sendStatsEvent("audio", !P3.audio.isMuted() ? "on" : "off");
    }

    onAchievementsButtonClick(button)
    {
        this.setAllButtonsEnabled(false);
        this.signals.achievements.dispatch(this);
    }

    onLeftButtonClick(button) {
        // override
    }

    onRightButtonClick(button) {
        // override
    }

    onSkipButtonClick(button) {
        this.setAllButtonsEnabled(false);
        this.signals.next.dispatch(this);
    }

    onUserSlotButtonClick(button) {
        // override
    }

    onCalculatorButtonClick(button) {
        // override
    }
}

GelScene.SIZE_SMALL = "small";
GelScene.SIZE_MEDIUM = "medium";
GelScene.SIZE_LARGE = "large";